import React, { CSSProperties } from 'react'
import { connect } from "react-redux"
import * as d3 from "d3"
import trans from "trans"
import { ItemInterface, ReactSortable } from "react-sortablejs"

import {
    updateInputValue as change,
} from "app/reducers/form";

import {
    css,
    Button,
    TextInput,
    Theme,
    Grid,
    GridCell,
    Icon,
    Toggler,
} from "@bdfx/mergado-ui-kit"

import { Slider } from "@mui/material";

import Shape, { SOURCE_IMAGE_TYPE } from "shape-entities/types/shape"
import { getShapeByType, renderClipMaskShape } from "template-edit/components/helper"
import { PathPoint } from 'shape-entities/types/PathShape'
import { AnyPoint } from 'app/types/model-types';
import { CoordsType } from 'shape-entities/components/shape'
import { LineCapType, LineJoinType, addClass, getCursorByType,  getFormName, getShapeSize, hasClass, recalcShapeSizeAndPointsPositions } from './helpers';
import { isJson } from 'app/helpers/other'
import ImagickFontSelect from 'shape-edit/components/imagickFontSelect'
import { URL } from 'app/config/api-config'
import { IconArrows } from 'app/icons'
import { DELETE_KEY } from 'app/helpers/keymap'
import { ESCAPE_KEY } from 'app/helpers/keymap'
import { selectFormValues } from 'app/reducers/form'
import { setHelper } from "actions";
import { EditModeType, HelperType } from 'app/types/app-types'
import { COPY_CURSOR } from 'template-edit/containers/image-preview'
import { SizeType } from 'products/components/filters'
import { NumericInput } from './fields'
import { debounce } from 'lodash'
import { DO_NOT_SAVE_HISTORY } from 'template-edit/sagas/undoRedoHistory'
import { TemplateOrAlternativeTemplateType } from 'template-edit/undoRedoReducers'
export type HandleRefType = "topleft" | "topright" | "bottomleft" | "bottomright" | "center"
export type ClipMaskObjectType = 'rect' | 'circle' | 'bezier' | 'polygon' | 'text' | 'ellipse'
export type ClipMaskObject = {
    type: ClipMaskObjectType
    x: number
    y: number
    width: number
    height: number
    padding?: number
    angle: number
    isFilled: boolean
    points: PathPoint[] | CoordsType[] | AnyPoint[]
    strokeWidth: number
    lineCap: LineCapType
    lineJoin: LineJoinType
    fontFamily: string
    fontSize: number
    textContent: string
}

export const EmptyClipMaskObject: ClipMaskObject = {
    type: null,
    x: null,
    y: null,
    width: null,
    height: null,
    padding: undefined,
    angle: null,
    isFilled: null,
    points: null,
    strokeWidth: null,
    lineCap: null,
    lineJoin: null,
    fontFamily: null,
    fontSize: null,
    textContent: null,
} as const

type ClipMaskObjectProperty = keyof typeof EmptyClipMaskObject
type PathPointHandleType = 'point' | 'c' | 'c2'

interface State {
    maskObjects: ClipMaskObject[]
    activeObjectId: number
    refType: HandleRefType
    deltaCenterX: number
    deltaCenterY: number
    scaleX: number
    scaleY: number
    box: DOMRect
    c: number
    s: number
    draggedPointIndex: number
    draggedPointType: PathPointHandleType
    isNew: boolean
}

interface Props {
    shape: Shape
    isAlt: boolean
    form: TemplateOrAlternativeTemplateType
    helper: HelperType
    editMode: EditModeType
    activeShapeId: string
    change: typeof change
    setHelper: typeof setHelper
}

class EditClipMaskModal extends React.Component<Props, State> {
    handleRefs: React.RefObject<SVGGElement | SVGRectElement | SVGImageElement>[] = []
    wrapperRef: React.RefObject<SVGRectElement>
    rectRef: React.RefObject<SVGRectElement>
    pointsRef: React.RefObject<SVGRectElement>[] = []
    objectsRefs: React.RefObject<SVGGElement>[] = []

    constructor(props: Props) {
        super(props)

        this.state = {
            maskObjects: props.shape.clip_mask && isJson(props.shape.clip_mask) ? JSON.parse(props.shape.clip_mask) : [],
            refType: null,
            activeObjectId: 0,
            deltaCenterX: 0,
            deltaCenterY: 0,
            scaleX: 1,
            scaleY: 1,
            box: null,
            c: 1,
            s: 0,
            draggedPointIndex: 0,
            draggedPointType: null,
            isNew: null,
        }

        this.wrapperRef = React.createRef()
        this.rectRef = React.createRef()
    }

    componentDidMount() {
        this.bindDragEvents()
        document.addEventListener("keydown", this.handleKeyDown)
        this.props.setHelper({ ...this.props.helper, isMaximized: false, closedAt: this.props.helper.closedAt ? this.props.helper.closedAt : Date.now() })

        const elements = document.getElementsByClassName('react-confirm-alert-overlay')
        const el = elements[0] ?? null

        if (el && !hasClass(el, 'clip-mask-modal')) {
            addClass(el, 'clip-mask-modal')
        }
    }

    componentWillUnmount(): void {
        document.removeEventListener("keydown", this.handleKeyDown)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        const { activeObjectId, maskObjects } = this.state

        if (prevState && prevState.maskObjects && (
            (prevState.maskObjects?.length !== this.state.maskObjects?.length || this.state.activeObjectId !== prevState.activeObjectId) ||
            ((activeObjectId !== null && maskObjects[activeObjectId] && (
                !prevState || !prevState.maskObjects || !prevState.maskObjects[activeObjectId] || !prevState.maskObjects[activeObjectId].points ||
                prevState.maskObjects[activeObjectId].points?.length !== maskObjects[activeObjectId].points?.length
            )))
        )) {
            this.bindDragEvents()
        }
    }

    getRef = (objectId: number, handleRef: HandleRefType): React.RefObject<any> => {
        const refString = `clip_mask_object_${objectId}_${handleRef}`
        if (typeof this.handleRefs[refString] === 'undefined') {
            this.handleRefs[refString] = React.createRef()
        }
        return this.handleRefs[refString]
    }

    getEl = (objectId: number, handleRef: HandleRefType): SVGGElement | SVGRectElement | SVGImageElement => {
        const ref = this.getRef(objectId, handleRef)
        return ref.current
    }

    getPointRef = (objectIndex: number, pointIndex: number, pointType: PathPointHandleType): React.RefObject<SVGRectElement> => {
        const refString = `bezier_point_${objectIndex}_${pointIndex}_${pointType}`
        if (typeof this.pointsRef[refString] === 'undefined') {
            this.pointsRef[refString] = React.createRef()
        }
        return this.pointsRef[refString]
    }

    getPointEl = (objectIndex: number, pointIndex: number, pointType: PathPointHandleType): SVGRectElement => {
        const ref = this.getPointRef(objectIndex, pointIndex, pointType)
        return ref.current
    }

    getObjectRef = (objectIndex: number): React.RefObject<any> => {
        const refString = `object_${objectIndex}`
        if (typeof this.objectsRefs[refString] === 'undefined') {
            this.objectsRefs[refString] = React.createRef()
        }
        return this.objectsRefs[refString]
    }

    getObjectEl = (objectIndex: number): any => {
        const ref = this.getObjectRef(objectIndex)
        return ref.current
    }

    getIconByType = (type: ClipMaskObjectType): React.JSX.Element => {
        switch (type) {
            case 'circle':
                return <img src={`${URL.publicPath}img/icons/clip-mask-circle.svg`} className={'icon-img'} />
            case 'rect':
                return <img src={`${URL.publicPath}img/icons/clip-mask-rectangle.svg`} className={'icon-img'} />
            case 'text':
                return <img src={`${URL.publicPath}img/icons/clip-mask-text.svg`} className={'icon-img'} />
            case 'bezier':
                return <img src={`${URL.publicPath}img/icons/clip-mask-bezier.svg`} className={'icon-img'} />
            default:
                return <div />
        }
    }

    getSize = (): SizeType => {
        const { shape } = this.props
        return getShapeSize(shape)
    }

    handleKeyDown = (event) => {
        const { maskObjects, activeObjectId, draggedPointIndex } = this.state

        if (event.target.type || (event.target.tagName && event.target.tagName === 'INPUT')) {
            return;
        }

        const maskObject = activeObjectId !== null ? (maskObjects[activeObjectId] ?? null) : null
        const isBezier = maskObject?.type === 'bezier'

        if (!event.ctrlKey) {
            switch (event.keyCode) {
                case ESCAPE_KEY:
                    event.preventDefault()
                    this.setActiveObjectId(null)
                    break;
                case DELETE_KEY:
                    if (activeObjectId !== null && maskObject) {
                        event.preventDefault()

                        if (isBezier) {
                            if (draggedPointIndex !== null) {
                                let updatedPoints = []
                                let updatedObjects = [...maskObjects]
                                maskObject.points.forEach((point: PathPoint, idx) => {
                                    if (idx !== draggedPointIndex) {
                                        updatedPoints.push(point)
                                    }
                                })
                                updatedObjects[activeObjectId].points = updatedPoints
                                this.setState({
                                    draggedPointIndex: null,
                                    maskObjects: updatedObjects,
                                })
                            }
                        } else {
                            this.handleDeleteIdx(activeObjectId)
                        }
                    }
                    break;
                default:
                    //console.debug('other key', event)
                    break;
            }
        }

    }

    handleRightClick = (event) => {
        event.preventDefault()
        this.setActiveObjectId(null)
    }

    dragStarted = (event, draggedObjectId: number, refType: HandleRefType) => {
        const { maskObjects } = this.state

        const { width, height } = this.getSize()

        const wrapper = this.wrapperRef?.current
        const box = wrapper.getBoundingClientRect()

        const scaleX = box.width / width
        const scaleY = box.height / height

        const newX = (event.sourceEvent.clientX - box.left) / scaleX
        const newY = (event.sourceEvent.clientY - box.top) / scaleY

        const draggedMaskObject = maskObjects[draggedObjectId]
        const alpha = -(draggedMaskObject.angle || 0) / 180 * Math.PI

        this.setState({
            activeObjectId: draggedObjectId,
            refType: refType,
            deltaCenterX: Math.round(newX - draggedMaskObject.x),
            deltaCenterY: Math.round(newY - draggedMaskObject.y),
            scaleX: scaleX,
            scaleY: scaleY,
            box: box,
            c: Math.cos(alpha),
            s: Math.sin(alpha),
        })
    }

    dragged = (event) => {
        const { maskObjects, activeObjectId, refType, deltaCenterX, deltaCenterY, scaleX, scaleY, box } = this.state

        const draggedObject: ClipMaskObject = { ...maskObjects[activeObjectId] }

        const newX = Math.round((event.sourceEvent.clientX - box.left) / scaleX)
        const newY = Math.round((event.sourceEvent.clientY - box.top) / scaleY)

        const rotatedCenterPos = this.rotateByAngle({ x: draggedObject.x, y: draggedObject.y })
        const rotatedNewPos = this.rotateByAngle({ x: newX, y: newY })

        const newWidth = 2 * Math.abs(rotatedCenterPos.x - rotatedNewPos.x)
        const newHeight = 2 * Math.abs(rotatedCenterPos.y - rotatedNewPos.y)

        const keepRatio = draggedObject.type === 'circle'

        const middleSize = Math.round((newWidth + newHeight) / 2)

        let updatedObjects = [...maskObjects]

        if (refType === 'center') {
            updatedObjects[activeObjectId].x = Math.round(newX - deltaCenterX)
            updatedObjects[activeObjectId].y = Math.round(newY - deltaCenterY)
        } else {
            const scaleX = newWidth / maskObjects[activeObjectId].width
            const scaleY = newHeight / maskObjects[activeObjectId].height

            const previousWidth = maskObjects[activeObjectId].width
            const previousHeight = maskObjects[activeObjectId].height

            updatedObjects[activeObjectId].width = keepRatio ? middleSize : newWidth
            updatedObjects[activeObjectId].height = keepRatio ? middleSize : newHeight

            switch (draggedObject.type) {
                case 'text':
                    const prevMiddleSize = Math.round((previousWidth + previousHeight) / 2)
                    const r = middleSize / prevMiddleSize
                    updatedObjects[activeObjectId].fontSize = r * updatedObjects[activeObjectId].fontSize
                    this.updateTextSizeDebounced()
                    break;
                case 'bezier':
                    updatedObjects[activeObjectId].points.forEach((point: PathPoint, key: number) => {
                        const updatedPoint: PathPoint = {
                            ...point,
                            x: point.x * scaleX,
                            y: point.y * scaleY,
                            cx: point.cx * scaleX,
                            cy: point.cy * scaleY,
                        }
                        updatedObjects[activeObjectId].points[key] = updatedPoint
                    })
                    break;
            }
        }

        this.setState({ maskObjects: updatedObjects })
    }

    dragEnded = (event) => {
        const { maskObjects } = this.state
        const { shape, isAlt } = this.props
        const formName = getFormName(isAlt)

        this.setState({
            refType: null,
            deltaCenterX: null,
            deltaCenterY: null,
        })

        this.props.change(formName, `shapes.${shape.index}.clip_mask`, maskObjects && Array.isArray(maskObjects) && maskObjects.length > 0 ? JSON.stringify(maskObjects) : null)
    }

    bindDragEvents = () => {
        const { maskObjects, activeObjectId } = this.state
        const handleRefTypes: HandleRefType[] = ["topleft", "topright", "bottomleft", "bottomright", "center"]

        maskObjects.forEach((maskObject, objectIndex) => {
            const isBezier = maskObject.type === 'bezier' && maskObject.points && Array.isArray(maskObject.points)
            const isActive = activeObjectId === objectIndex
            const angle = maskObject.angle || 0

            if (isActive) {
                //handles and center:
                handleRefTypes.forEach(handleRefType => {
                    const el = this.getEl(objectIndex, handleRefType)
                    if (el && d3.select(el)) {
                        d3.select(el)
                            .style("cursor", getCursorByType(handleRefType, angle))
                            .call(d3.drag()
                                .on("start", (event) => this.dragStarted(event, objectIndex, handleRefType))
                                .on("drag", this.dragged)
                                .on("end", this.dragEnded)
                            )
                            .on("contextmenu", this.handleRightClick)
                    }
                })

                //point events for active bezier:
                if (isBezier) {
                    const el = this.rectRef.current
                    if (el && d3.select(el)) {
                        d3.select(el)
                            .style("cursor", COPY_CURSOR)
                            .call(d3.drag()
                                .on("start", (event) => this.bezierDragStarted(event, 'point', null))
                                .on("drag", this.bezierDragged)
                                .on("end", this.bezierDragEnded)
                            )
                            .on("contextmenu", this.handleRightClick)
                    }

                    maskObject.points.forEach((point, pointIndex) => {
                        this.bindPathPointEvents(objectIndex, pointIndex)
                    })
                }
            }

            //all objects events:
            const objectEl = this.getObjectEl(objectIndex)
            if (objectEl && objectEl.nodeType === Node.ELEMENT_NODE && d3.select(objectEl)) {   //is node or MoveablePoints???
                d3.select(objectEl)
                    .style("cursor", getCursorByType('move', maskObject.angle || 0))
                    .call(d3.drag()
                        .on("start", (event) => this.dragStarted(event, objectIndex, 'center'))
                        .on("drag", this.dragged)
                        .on("end", this.dragEnded)
                    )
                    .on("contextmenu", this.handleRightClick)
            }
        })
    }

    updateTextSizeDebounced = debounce(() => {
        const { activeObjectId, maskObjects } = this.state
        const { shape, isAlt, form } = this.props

        const { width, height } = this.getSize()
        const wrapper = this.wrapperRef?.current
        const box = wrapper.getBoundingClientRect()

        const formName = getFormName(isAlt)

        const scaleX = box.width / width
        const scaleY = box.height / height

        let updatedMaskObjects = [...maskObjects]
        if (updatedMaskObjects[activeObjectId].type === 'text') {
            const el = this.getObjectEl(activeObjectId)
            if (el) {
                const clonedEl = el.cloneNode(true)
                clonedEl.setAttribute('transformation', undefined)  //no rotation!
                const activeObjectBox = clonedEl.getBoundingClientRect()
                updatedMaskObjects[activeObjectId].width = activeObjectBox.width / scaleX
                updatedMaskObjects[activeObjectId].height = activeObjectBox.height / scaleY
            }
        }
        this.setState({ maskObjects: updatedMaskObjects })
        this.props.change(formName, `shapes.${shape.index}.clip_mask`, updatedMaskObjects.length > 0 ? JSON.stringify(updatedMaskObjects) : null, DO_NOT_SAVE_HISTORY)
    }, 200)

    addObject = (newObject: ClipMaskObject) => {
        const { maskObjects } = this.state
        const { shape, isAlt } = this.props

        const formName = getFormName(isAlt)

        const length = maskObjects.length
        const updatedMaskObjects = [...maskObjects, newObject]
        this.setState({
            maskObjects: updatedMaskObjects,
            activeObjectId: length
        })
        if (newObject.type === 'text') {
            this.updateTextSizeDebounced()
        }
        this.props.change(formName, `shapes.${shape.index}.clip_mask`, updatedMaskObjects.length > 0 ? JSON.stringify(updatedMaskObjects) : null, DO_NOT_SAVE_HISTORY)
    }

    rotateByAngle = (coords: CoordsType, alphaRadians: number = null): CoordsType => {
        const c = alphaRadians !== null ? Math.cos(alphaRadians) : this.state.c
        const s = alphaRadians !== null ? Math.sin(alphaRadians) : this.state.s

        return {
            x: coords.x * c - coords.y * s,
            y: coords.x * s + coords.y * c,
        }
    }

    bezierDragStarted = (event, pointType: PathPointHandleType, draggedPointIndex: number) => {
        const { maskObjects, activeObjectId } = this.state
        const { shape, form, isAlt } = this.props

        const formName = getFormName(isAlt)

        const { width, height } = this.getSize()
        const wrapper = this.wrapperRef?.current
        const box = wrapper.getBoundingClientRect()

        const el: SVGElement = event.sourceEvent.target
        const isNewPoint = el.classList[0] !== 'resizable-handle'   //is a new point or just move existing? event.sourceEvent.target.tagName === 'rect'

        const draggedMaskObject = maskObjects[activeObjectId]
        const alpha = -((draggedMaskObject.angle || 0)) / 180 * Math.PI

        const scaleX = box.width / width
        const scaleY = box.height / height

        const newX = (event.sourceEvent.clientX - box.left) / scaleX
        const newY = (event.sourceEvent.clientY - box.top) / scaleY

        const rotatedNewPoint = this.rotateByAngle({ x: newX, y: newY }, alpha)
        const rotatedCenterPoint = this.rotateByAngle({ x: draggedMaskObject.x, y: draggedMaskObject.y }, alpha)

        const newPointBezier: PathPoint = {
            x: rotatedNewPoint.x - rotatedCenterPoint.x,
            y: rotatedNewPoint.y - rotatedCenterPoint.y,
            cx: rotatedNewPoint.x - rotatedCenterPoint.x,
            cy: rotatedNewPoint.y - rotatedCenterPoint.y,
        }

        const pointIndex = isNewPoint ? draggedMaskObject.points && Array.isArray(draggedMaskObject.points) && draggedMaskObject.points.length : (draggedPointIndex ? draggedPointIndex : 0)

        this.setState({
            draggedPointType: pointType,
            draggedPointIndex: pointIndex,
            isNew: isNewPoint,
            box: box,
            c: Math.cos(alpha),
            s: Math.sin(alpha),
            scaleX: scaleX,
            scaleY: scaleY,
        })

        if (isNewPoint) {
            const updatedBezierCalc = recalcShapeSizeAndPointsPositions(form, {
                type: 'bezier',
                angle: draggedMaskObject.angle,
                x: draggedMaskObject.x,
                y: draggedMaskObject.y,
                width: draggedMaskObject.width,
                height: draggedMaskObject.height,
                points: [...draggedMaskObject.points, newPointBezier],
                name: null,
                priority: null,
                is_draggable: null,
            })

            let updatedMaskObjects = [...maskObjects]
            updatedMaskObjects[activeObjectId] = {
                ...maskObjects[activeObjectId],
                x: updatedBezierCalc.x,
                y: updatedBezierCalc.y,
                width: updatedBezierCalc.width,
                height: updatedBezierCalc.height,
                points: updatedBezierCalc.points,
            }

            this.setState({ maskObjects: updatedMaskObjects })
            this.props.change(formName, `shapes.${shape.index}.clip_mask`, JSON.stringify(updatedMaskObjects), DO_NOT_SAVE_HISTORY)
        } else {    //drag an existing point

        }
    }

    bezierDragged = (event) => {
        const { isNew, draggedPointType, maskObjects, activeObjectId, draggedPointIndex, box, scaleX, scaleY, deltaCenterX, deltaCenterY } = this.state
        const draggedMaskObject = maskObjects[activeObjectId]

        const newX = (event.sourceEvent.clientX - box.left) / scaleX - draggedMaskObject.x
        const newY = (event.sourceEvent.clientY - box.top) / scaleY - draggedMaskObject.y

        const rotatedPoint = this.rotateByAngle({ x: newX, y: newY })

        const point = draggedMaskObject.points[draggedPointIndex] as PathPoint
        let updatedPoints = [...draggedMaskObject.points] as PathPoint[]

        if (isNew) {
            updatedPoints[draggedPointIndex].cx = 2 * point.x - rotatedPoint.x
            updatedPoints[draggedPointIndex].cy = 2 * point.y - rotatedPoint.y
        } else {
            switch (draggedPointType) {
                case 'c':
                    updatedPoints[draggedPointIndex].cx = rotatedPoint.x
                    updatedPoints[draggedPointIndex].cy = rotatedPoint.y
                    break;
                case 'c2':
                    updatedPoints[draggedPointIndex].cx = 2 * point.x - rotatedPoint.x
                    updatedPoints[draggedPointIndex].cy = 2 * point.y - rotatedPoint.y
                    break;
                case 'point':
                    updatedPoints[draggedPointIndex].cx += (rotatedPoint.x - point.x)
                    updatedPoints[draggedPointIndex].cy += (rotatedPoint.y - point.y)
                    updatedPoints[draggedPointIndex].x = rotatedPoint.x
                    updatedPoints[draggedPointIndex].y = rotatedPoint.y

                    break;
            }
        }

        let updatedMaskObjects = [...maskObjects]
        updatedMaskObjects[activeObjectId].points = updatedPoints
        this.setState({ maskObjects: updatedMaskObjects })
    }

    bezierDragEnded = (event) => {
        const { shape, isAlt } = this.props
        const { maskObjects } = this.state
        const formName = getFormName(isAlt)
        this.props.change(formName, `shapes.${shape.index}.clip_mask`, JSON.stringify(maskObjects), DO_NOT_SAVE_HISTORY)
    }

    bindPathPointEvents = (objectIndex: number, pointIndex: number) => {
        const allPointTypes: PathPointHandleType[] = ['point', 'c', 'c2']
        allPointTypes.forEach(pointType => {
            const el = this.getPointEl(objectIndex, pointIndex, pointType)
            if (el && d3.select(el)) {
                d3.select(el)
                    .style("cursor", getCursorByType('move'))
                    .call(
                        d3.drag()
                            .on("start", (event) => this.bezierDragStarted(event, pointType, pointIndex))
                            .on("drag", (event) => this.bezierDragged(event))
                            .on("end", (event) => this.bezierDragEnded(event))
                    );
            }
        })
    }

    renderObjects = (noHandles: boolean) => {
        const { maskObjects, activeObjectId, draggedPointIndex } = this.state

        if (!maskObjects || maskObjects.length === 0) {
            return <g />
        }

        return <React.Fragment>
            {maskObjects.map((maskObject, idx) => {
                const isActive = activeObjectId !== null && idx === activeObjectId
                return (noHandles || !isActive) ? this.renderObject(maskObject, idx, noHandles) : <React.Fragment key={`render_object_${idx}`}></React.Fragment>
            })}

            {!noHandles && activeObjectId !== null &&
                this.renderObject(maskObjects[activeObjectId], activeObjectId, noHandles)
            }
        </React.Fragment>
    }

    renderObject = (maskObject: ClipMaskObject, idx: number, noHandles: boolean) => {
        const { activeObjectId, draggedPointIndex } = this.state
        const { width, height } = this.getSize()

        const {form,isAlt} = this.props

        const isInvertClipMask = !!form.shapes[this.props.shape.index].invert_clip_mask

        const handleSize = (width + height) / 100
        const dashArray = `${handleSize} ${handleSize / 2}`

        const lineStyle = {
            strokeWidth: '1px',
            stroke: Theme.steelblue,
        }

        const isActive = activeObjectId !== null && idx === activeObjectId
        const isBezier = maskObject.type === 'bezier'

        return <React.Fragment key={`object_${maskObject.type}_${idx}_${isInvertClipMask}`}>
            {(isActive && !noHandles && isBezier) &&
                <rect
                    ref={this.rectRef}
                    width={width}
                    height={height}
                    fill={'#0000ff5e'}
                    style={{
                        cursor: 'copy',
                        pointerEvents: 'auto',
                    }}
                />
            }

            {renderClipMaskShape(maskObject, !noHandles, isInvertClipMask, 0, 0, !noHandles ? this.getObjectRef(idx) : undefined)}

            {(isActive && !noHandles && isBezier) &&
                <g transform={`translate(${maskObject.x}, ${maskObject.y}) rotate(${maskObject.angle || 0})`}>

                    {maskObject.points.map((point: PathPoint, pointIndex: number) => {
                        const cx2 = 2 * point.x - point.cx
                        const cy2 = 2 * point.y - point.cy
                        const isActivePoint = draggedPointIndex && draggedPointIndex === pointIndex

                        return <g key={`point_key_${idx}_${pointIndex}`}>
                            <rect
                                ref={this.getPointRef(idx, pointIndex, 'point')}
                                className={`resizable-handle`}
                                x={-.5 * handleSize + point.x}
                                y={-.5 * handleSize + point.y}
                                width={handleSize}
                                height={handleSize}
                                fill={isActivePoint ? 'transparent' : Theme.red}
                                stroke={Theme.red}
                                strokeWidth={1}
                            />

                            {isActivePoint &&
                                <circle
                                    className={`resizable-handle`}
                                    style={{ pointerEvents: 'none' }}
                                    cx={point.x}
                                    cy={point.y}
                                    r={.6 * handleSize}
                                    stroke={Theme.orange}
                                    strokeWidth={2}
                                    fill={'none'}
                                />
                            }

                            <line x1={point.cx} y1={point.cy} x2={point.x} y2={point.y} style={lineStyle} strokeDasharray={dashArray} />
                            <rect
                                ref={this.getPointRef(idx, pointIndex, 'c')}
                                className={`resizable-handle`}
                                x={-.5 * .8 * handleSize + point.cx}
                                y={-.5 * .8 * handleSize + point.cy}
                                width={.8 * handleSize}
                                height={.8 * handleSize}
                                fill={Theme.blue}
                            />

                            <line x1={cx2} y1={cy2} x2={point.x} y2={point.y} style={lineStyle} strokeDasharray={dashArray} />
                            <rect
                                ref={this.getPointRef(idx, pointIndex, 'c2')}
                                className={`resizable-handle`}
                                x={-.5 * .8 * handleSize + cx2}
                                y={-.5 * .8 * handleSize + cy2}
                                width={.8 * handleSize}
                                height={.8 * handleSize}
                                fill={Theme.blue}
                            />
                        </g>
                    })}
                </g>
            }

            {(isActive && !noHandles) &&
                <g transform={`translate(${maskObject.x - maskObject.width / 2}, ${maskObject.y - maskObject.height / 2}) rotate(${maskObject.angle || 0}, ${maskObject.width / 2}, ${maskObject.height / 2})`}>
                    <rect
                        strokeDasharray={dashArray}
                        width={maskObject.width}
                        height={maskObject.height}
                        fill={'transparent'}
                        stroke={Theme.steelblue}
                        strokeWidth={handleSize / 20}
                        style={{
                            cursor: 'pointer',
                            pointerEvents: 'none',
                        }}
                    />
                    <rect
                        ref={this.getRef(idx, 'topleft')}
                        className={`resizable-handle topleft`}
                        width={handleSize}
                        height={handleSize}
                        transform={`translate(${-.5 * handleSize}, ${-.5 * handleSize})`}
                    />
                    <rect
                        ref={this.getRef(idx, 'topright')}
                        className={`resizable-handgetRefle topright`}
                        width={handleSize}
                        height={handleSize}
                        transform={`translate(${maskObject.width - .5 * handleSize}, ${-.5 * handleSize})`}
                    />
                    <rect
                        ref={this.getRef(idx, 'bottomleft')}
                        className={`resizable-handle bottomleft`}
                        width={handleSize}
                        height={handleSize}
                        transform={`translate(${-.5 * handleSize}, ${maskObject.height - .5 * handleSize})`}
                    />
                    <rect
                        ref={this.getRef(idx, 'bottomright')}
                        className={`resizable-handle bottomright`}
                        width={handleSize}
                        height={handleSize}
                        transform={`translate(${maskObject.width - .5 * handleSize}, ${maskObject.height - .5 * handleSize})`}
                    />
                    <g ref={this.getRef(idx, 'center')} transform={`translate(${.5 * maskObject.width - handleSize}, ${.5 * maskObject.height - handleSize})`}>
                        <IconArrows size={2 * handleSize} />
                    </g>
                </g>
            }
        </React.Fragment>
    }

    handleDeleteIdx = (idx: number) => {
        const { shape, form, isAlt } = this.props
        const { maskObjects, activeObjectId } = this.state
        const formName = getFormName(isAlt)

        const updatedMaskObjects = maskObjects.filter((maskObject, i) => i !== idx)

        this.setState({
            maskObjects: updatedMaskObjects,
            activeObjectId: null,
        })

        this.props.change(formName, `shapes.${shape.index}.clip_mask`, JSON.stringify(updatedMaskObjects))
    }

    handleDeleteAll = () => {
        const { shape, form, isAlt } = this.props
        const formName = getFormName(isAlt)
        this.setState({ maskObjects: [] })
        this.props.change(formName, `shapes.${shape.index}.clip_mask`, null)
    }

    updateValue = (fieldName: string, value: string | number | null, idx: number) => {
        const { shape, form, isAlt } = this.props
        const { maskObjects } = this.state

        const formName = getFormName(isAlt)

        if (Object.keys(EmptyClipMaskObject).includes(fieldName)) {
            let updatedMaskObjects = [...maskObjects]
            updatedMaskObjects[idx][fieldName] = value
            if (updatedMaskObjects[idx].type === 'text') {
                this.updateTextSizeDebounced()
            }

            this.setState({ maskObjects: updatedMaskObjects })
            this.props.change(formName, `shapes.${shape.index}.clip_mask`, JSON.stringify(updatedMaskObjects), DO_NOT_SAVE_HISTORY)
        } else {
            console.debug('Unknown field', fieldName)
        }
    }

    reorder = (list) => {
        const { maskObjects } = this.state
        const { shape, form, isAlt } = this.props

        const formName = getFormName(isAlt)

        const ids = maskObjects.map((maskObject, idx) => idx)
        const newOrder = list.filter(i => typeof i !== 'undefined').map(i => i.id)

        if (ids.join(',') !== newOrder.join(',')) {
            let updatedMaskObjects = []
            newOrder.forEach((idx) => {
                updatedMaskObjects.push(maskObjects[idx])
            })

            this.setState({ maskObjects: updatedMaskObjects })
            this.props.change(formName, `shapes.${shape.index}.clip_mask`, JSON.stringify(updatedMaskObjects), DO_NOT_SAVE_HISTORY)
        }
    }

    addRectangle = () => {
        const { width, height } = this.getSize()
        const middleSize = width + height / 2

        const newObject: ClipMaskObject = {
            ...EmptyClipMaskObject,
            type: 'rect',
            x: width / 2,
            y: height / 2,
            width: middleSize / 4,
            height: middleSize / 4,
        }
        this.addObject(newObject)
    }

    addCircle = () => {
        const { width, height } = this.getSize()
        const middleSize = width + height / 2

        const newObject: ClipMaskObject = {
            ...EmptyClipMaskObject,
            type: 'circle',
            x: width / 2,
            y: height / 2,
            width: middleSize / 4,
            height: middleSize / 4,
        }
        this.addObject(newObject)
    }

    addText = () => {
        const { width, height } = this.getSize()
        const middleSize = width + height / 2

        const newObject: ClipMaskObject = {
            ...EmptyClipMaskObject,
            type: 'text',
            x: width / 2,
            y: height / 2,
            width: middleSize / 4,
            height: middleSize / 4,
            textContent: 'ABC',
            fontSize: middleSize / 4,
            fontFamily: 'Anton-Regular',
        }
        this.addObject(newObject)
    }

    addBezier = () => {
        const { width, height } = this.getSize()
        const newObject: ClipMaskObject = {
            ...EmptyClipMaskObject,
            type: 'bezier',
            x: width / 2,
            y: height / 2,
            width: width,
            height: height,
            points: [],
        }

        this.addObject(newObject)
    }

    setActiveObjectId = (activeObjectId: number) => {
        this.setState({ activeObjectId: activeObjectId })
    }

    render() {
        const { shape, form, isAlt, editMode, activeShapeId } = this.props
        const { maskObjects, activeObjectId, scaleX, scaleY } = this.state

        const { width, height } = this.getSize()

        const formName = getFormName(isAlt)

        const selectedMaskObject = activeObjectId !== null && typeof maskObjects[activeObjectId] !== 'undefined' ? maskObjects[activeObjectId] : null
        const transformShape = shape.type !== SOURCE_IMAGE_TYPE ? `translate(${width / 2}, ${height / 2})` : undefined

        const ids = maskObjects.map((maskObject, idx) => idx)
        const list: ItemInterface[] = ids ? ids.map(id => { return { id: id, selected: false } }) : []

        const isInvertClipMask = form.shapes[shape.index].invert_clip_mask
        const clipPathUrl = `url(#clip_mask_${shape.index})`
        const maskPathUrl = `url(#xor_me_${shape.index})`

        return (
            <StyledGrid>
                <GridCell>
                    <Toggler
                        name={`${formName}.shapes.${shape.index}.invert_clip_mask`}
                        onLabel={trans('invert_clip_mask')}
                        style={{textAlign: 'left !important'}}
                    />

                    <ToolsBarGrid>
                        <GridCell className={'clickable'} title={trans('rectangle')} onClick={() => this.addRectangle()}>
                            {this.getIconByType('rect')}
                        </GridCell>

                        <GridCell className={'clickable'} title={trans('circle')} onClick={() => this.addCircle()}>
                            {this.getIconByType('circle')}
                        </GridCell>

                        <GridCell className={'clickable'} title={trans('text')} onClick={() => this.addText()}>
                            {this.getIconByType('text')}
                        </GridCell>

                        <GridCell className={'clickable'} title={trans('bezier')} onClick={() => this.addBezier()}>
                            {this.getIconByType('bezier')}
                        </GridCell>

                        <GridCell className={'unselectable'}  style={{ color: Theme.steelblue, background: 'transparent !important', border: '0px', padding: '4px' }}>
                            {Math.round(width)}x{Math.round(height)}
                        </GridCell>

                        <GridCell className='clickable' onClick={() => this.handleDeleteAll()} style={{padding: '4px 0'}}>
                            <Icon type={'trash-o'} size={iconSize} color={'#ccc'} />
                        </GridCell>
                    </ToolsBarGrid>

                    {ids &&
                        <ReactSortable list={list} setList={this.reorder} handle=".drag-handle" tag={'div'} className={'muk-grid sortable-grid'}>
                            {maskObjects.map((maskObject, idx) => {
                                const isText = maskObject.type === 'text'
                                const isPoint = maskObject.type === 'bezier'
                                const isActive = activeObjectId === idx
                                const className = `${isActive ? 'active' : ''}`

                                return <React.Fragment key={`form_${idx}_${activeObjectId}`}>
                                    <GridCell className={className} id={`${idx}`}>
                                        <Button
                                            type="void"
                                            size="tiny"
                                            color="nocolor"
                                            className="drag-handle"
                                            icon={<Icon type={'arrows-v'} style={{ cursor: 'move' }} color={Theme.blue} />}
                                        />
                                    </GridCell>
                                    <GridCell className={`clickable ${className}`} onClick={() => this.setActiveObjectId(idx)} style={{ width: '100px', textAlign: 'center' }}>
                                        {this.getIconByType(maskObject.type)}
                                    </GridCell>
                                    <GridCell className={className} style={{alignSelf: 'end !important'}}>
                                        {/* <Range
                                                value={Math.round(maskObject.angle || 0)}
                                                min={-360}
                                                max={360}
                                                step={1}
                                                default={0}
                                                placeholder={'0°'}
                                                onChange={(angle) => {
                                                    const newAngle = Math.round(angle > 360 ? 360 : (angle < -360 ? -360 : angle))
                                                    this.updateValue('angle', newAngle, idx)
                                                }}
                                                onClear={() => this.updateValue('angle', null, idx)}
                                            /> */}

                                        <Slider
                                            value={Math.round(maskObject.angle || 0)}
                                            onChange={(event: Event, angle: number) => {
                                                const newAngle = Math.round(angle > 360 ? 360 : (angle < -360 ? -360 : angle))
                                                this.updateValue('angle', newAngle, idx)
                                            }}
                                            min={-360}
                                            max={360}
                                            step={1}
                                            marks={[
                                                { value: -270 },
                                                { value: -180, label: '-180' },
                                                { value: -90 },
                                                { value: 0, label: '0' },
                                                { value: 90 },
                                                { value: 180, label: '180' },
                                                { value: 270 },
                                            ]}
                                            valueLabelDisplay="auto"
                                            getAriaLabel={() => `${trans('angle')}`}
                                            getAriaValueText={(value) => `${value}`}
                                            valueLabelFormat={(value) => `${value}`}
                                        />

                                        {isText &&
                                            <React.Fragment>
                                                {/* TODO: steel focus here on type, use value={this.state.textContent[idx]}*/}
                                                <TextInput
                                                    value={maskObject.textContent}
                                                    placeholder={trans('text')}
                                                    onChange={(value) => this.updateValue('textContent', value, idx)}
                                                    onClear={() => this.updateValue('textContent', null, idx)}
                                                />
                                                {/* <TextField
                                                        id="standard-basic"
                                                        label={trans('text')}
                                                        variant="standard"
                                                        value={maskObject.textContent}
                                                        placeholder={trans('text')}
                                                        onChange={(e) => {
                                                            const value = e.target.value
                                                            this.updateValue('textContent', value, idx)
                                                        }}
                                                        /> */}
                                                <Grid cols={'repeat(3, 1fr)'}>
                                                    <ImagickFontSelect
                                                        currentFontFamily={maskObject.fontFamily}
                                                        setFontFamily={(value: string) => this.updateValue('fontFamily', value, idx)}
                                                        sampleText={maskObject.textContent}
                                                    />

                                                    <GridCell title={trans('text_fontsize')}>
                                                        <NumericInput
                                                            formName={formName}
                                                            fieldName={'text_fontsize'}
                                                            placeholder={'text_fontsize'}
                                                            id={'text_fontsize'}
                                                            style={{ width: '100px' }}
                                                            min={1}
                                                            max={1000}
                                                            step={2}
                                                            units={'px'}
                                                            value={maskObject.fontSize}
                                                            changeValueMethod={(newSize) => {
                                                                const fontSize = parseInt(`${newSize}`.replace(/[^0-9]/g, ''))
                                                                if (fontSize) {
                                                                    this.updateValue('fontSize', fontSize, idx)
                                                                }
                                                                return fontSize
                                                            }}
                                                            onlyPositive={true}
                                                        />
                                                    </GridCell>

                                                </Grid>
                                            </React.Fragment>
                                        }

                                        {/* {isPoint &&
                                                <code style={{ display: 'block', height: '150px', overflowY: 'auto' }}>
                                                    {JSON.stringify(maskObject.points, null, "\t")}
                                                </code>
                                            } */}
                                    </GridCell>
                                    <GridCell className={`clickable ${className}`} onClick={() => this.handleDeleteIdx(idx)}>
                                        <Icon type={'trash-o'} color={Theme.red} size={22} />
                                    </GridCell>
                                </React.Fragment>
                            })}
                        </ReactSortable>
                    }
                </GridCell>

                <GridCell>
                    <svg version="1.1" viewBox={`0 0 ${width} ${height}`} width={width} height={height} className={'clip-mask-svg'} style={{ width: '100%', height: 'auto' }}>
                        <rect ref={this.wrapperRef} x={0} y={0} width={width} height={height} fill={isInvertClipMask ? 'white' : Theme.blue} stroke={Theme.grey} />
                        <g transform={transformShape}>
                            {getShapeByType(shape, { width: width, height: height }, false, activeShapeId, editMode)}
                        </g>
                        {this.renderObjects(false)}
                    </svg>
                </GridCell>

                <GridCell className='checker'>
                    <svg version="1.1" viewBox={`0 0 ${width} ${height}`} width={width} height={height} className={'no-events'} style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 200px)' }}>
                        <defs>
                            <clipPath id={`clip_mask_${shape.index}`}>
                                {this.renderObjects(true)}
                            </clipPath>

                            <mask id={`xor_me_${shape.index}`}>
                                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                                {this.renderObjects(true)}
                            </mask>

                        </defs>

                        <rect x={0} y={0} width={width} height={height} fill={'transparent'} stroke={Theme.grey} strokeWidth={3} />

                        {isInvertClipMask ?
                            <g mask={maskPathUrl}>
                                <g transform={transformShape}>
                                    {getShapeByType(shape, { width: width, height: height }, false, activeShapeId, editMode)}
                                </g>
                            </g> :
                            <g clipPath={clipPathUrl}>
                                <g transform={transformShape}>
                                    {getShapeByType(shape, { width: width, height: height }, false, activeShapeId, editMode)}
                                </g>
                            </g>
                        }

                    </svg>
                </GridCell>
            </StyledGrid>
        )
    }
}

const StyledGrid = css(Grid)({
    gridGap: '20px',
    gridTemplateColumns: 'repeat(3, 1fr)',
    minHeight: '50vh',
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
    },
    ' .clip-mask-svg': {
        width: 'auto',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: 'calc(100vh - 200px)',
    },
    ' .no-events': {
        pointerEvents: 'none',
    },
    ' div.active': {
        background: '#f2fafb',
    },
    ' .sortable-grid': {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, auto) 60px',
    },
})

const StyledTable = css('table')({
    ' .icon-img': {
        width: '32px',
        height: '32px',
    }
})

const ToolsBarGrid = css(Grid)({
    gridTemplateColumns: 'repeat(4, 90px) auto 80px',
    gridGap: '10px',
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'center',
        border: `1px solid ${Theme.steelblue}`,
        background: Theme.steelblue,
        borderRadius: Theme.radius,
        paddingTop: '4px',
    },
    ' .icon-img': {
        width: '25px',
        height: '25px',
    }
})

const iconSize = 22

function mapStateToProps(state) {
    return {
        helper: state.helper,
        isAlt: state.isAlt,
        editMode: state.editMode,
        activeShapeId: state.activeShapeId,
        form: selectFormValues(state, getFormName(state.isAlt)),
    }
}

export default connect(mapStateToProps, {
    change,
    setHelper,
})(EditClipMaskModal)
